import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import gql from 'graphql-tag'
import { useMutation } from '@apollo/client'
import SpecBlock from '@components/SpecBlock'
import Benefit from '@components/Benefit'
import Price from '@components/Price'
import NoticeBubble from '@components/NoticeBubble'
import SmartLink from '@components/SmartLink'
import LazyImage from '@/components/LazyImage'
import useTracking from '@/lib/tracking/useTracking'
import useLiveInfo from '@/hooks/useLiveInfo'
import useTrackingInfo from '@/hooks/useTrackingInfo'
import useRandomNextItem from '@/hooks/useRandomNextItem'
import useShortenCurrentUrl from '@/hooks/useShortenCurrentUrl'
import useScrollSideX from './useScrollSideX'
import getSimplifiedTotalBuyers, { DisplayRule } from './getSimplifiedTotalBuyers'
import styles from './index.module.sass'
import { useSocialInfo } from '@/views/bag/hooks/useSocialInfo'

const DEFAULT_IMAGE = '/images/img-emptyDefault.svg'

const RECORD_PRODUCT_CLICKING = gql`
  mutation record($broadcastId: String!, $productId: String!) {
    productClicking(broadcastId: $broadcastId, productId: $productId)
  }
`

export enum ProductViewType {
  // eslint-disable-next-line no-unused-vars
  Single = 'Single',
  // eslint-disable-next-line no-unused-vars
  MultipleSpecs = 'MultipleSpecs',
  // eslint-disable-next-line no-unused-vars
  MultiplePrices = 'MultiplePrices',
}

function getViewType (product): ProductViewType {
  const items = product?.items || []
  if (items.length <= 1) {
    return ProductViewType.Single
  }

  const firstItemPrice = items[0].discountedPrice
  const hasDifferentPrice = items.some(
    (item) => firstItemPrice !== item.discountedPrice
  )

  return hasDifferentPrice
    ? ProductViewType.MultiplePrices
    : ProductViewType.MultipleSpecs
}

const LiveProduct = ({ product, isPinned = false }) => {
  const { t } = useTranslation()
  const { data } = useSocialInfo()
  const shortenUrl = useShortenCurrentUrl()
  const socialInfo = useMemo(() => data?.socialInfo, [data])

  const info = useMemo(() => {
    if (!product?.productId) return {}
    const content = socialInfo?.clickingMetrics?.products?.[product.productId]
    return content || {}
  }, [socialInfo, product?.productId])

  const viewers = useMemo(() => {
    return info.latestViewers || []
  }, [info])

  const userCount = useMemo(() => {
    return info.userCount || 0
  }, [info])

  const saleWording = useMemo(() => {
    const wordingItem = getSimplifiedTotalBuyers(userCount)

    switch (wordingItem?.type) {
      case DisplayRule.SOME:
        return t('client.shoplist.desc.popularity2', { count: wordingItem.value })
      case DisplayRule.FEW:
        return t('client.shoplist.desc.popularity3', { count: wordingItem.value })
      case DisplayRule.MANY:
        return t('client.shoplist.desc.popularity1')
      default:
        return null
    }
  }, [userCount, t])

  const { item, clear, concat, next } = useRandomNextItem(10000)

  useEffect(() => {
    clear()
    concat(viewers)
  }, [viewers])

  const { broadcastId, serviceId, shopId } = useLiveInfo()
  const { screenName } = useTrackingInfo()
  const { tracker } = useTracking()

  const [productClicking] = useMutation(RECORD_PRODUCT_CLICKING, {
    errorPolicy: 'ignore'
  })
  const outClick = useCallback(() => {
    productClicking({
      variables: {
        broadcastId,
        productId: product?.productId
      }
    })

    tracker?.event({
      config: { utsType: 'click' },
      event: {
        screenName,
        category: 'product_list',
        params: {
          target: 'buy',
          broadcast_id: broadcastId,
          shop_id: shopId,
          product_id: product?.productId
        }
      }
    })
  }, [productClicking, broadcastId, product?.productId, tracker, shopId, screenName])
  const landingUrl = useMemo(() => {
    if (!product?.landingUrl || !broadcastId) return ''
    const url = new URL(product.landingUrl)
    url.searchParams.set('utm_source', 'livecommerce')
    url.searchParams.set('utm_campaign', broadcastId)
    if (serviceId === 'MYSHOP') {
      url.searchParams.set('redirectUrl', shortenUrl)
    }
    return url.toString()
  }, [product?.landingUrl, broadcastId, shortenUrl, serviceId])

  const items = useMemo(() => product?.items || [], [product?.items])
  const [displayItem, setItem] = useState(items[0] || null)
  useEffect(() => {
    if (!displayItem?.productItemId || !items.length) return
    const targetItem = items.find(item => item.productItemId === displayItem.productItemId)
    if (targetItem) setItem(targetItem)
  }, [items, displayItem?.productItemId])

  const scrollingRef = useRef(null)
  const { isStartSide, isEndSide } = useScrollSideX(scrollingRef)

  const price = useMemo(() => displayItem?.discountedPrice || displayItem?.price, [displayItem])
  const originalPrice = useMemo(() => displayItem?.price, [displayItem])

  const type = useMemo(() => getViewType(product), [product])
  const hasMultipleSpecs = useMemo(() => product?.items?.length > 1, [product?.items])

  const pinnedProductStyle = useMemo(() => clsx(
    isPinned ? styles.imageActive : styles.image
  ), [isPinned])

  const specListStyle = useMemo(() => clsx(
    styles.name,
    hasMultipleSpecs ? styles.singleLine : ''
  ), [hasMultipleSpecs])

  const specWrapperStyle = useMemo(() => clsx(
    styles.specWrapper,
    {
      [styles.startShadow]: !isStartSide,
      [styles.endShadow]: !isEndSide
    }
  ), [isStartSide, isEndSide])

  const noticeBubbleStyle = useMemo(() => clsx(styles.noticeBubble, item ? styles.show : null), [item])

  return (
    <div className={styles.item}>
      <SmartLink
        href={landingUrl}
        className={pinnedProductStyle}
        onClick={outClick}
      >
        <div className={styles.liveBadge}>
          <span>{t('client.shoplist.desc.itemonair')}</span>
        </div>
        <LazyImage
          className="responsiveImg"
          height="84px"
          width="84px"
          src={displayItem?.imageUrl || product.imageUrl}
          defaultSrc={DEFAULT_IMAGE}
          alt={product.name}
        />
      </SmartLink>
      <div className={styles.itemInfo}>
        <div className={styles.subGroup}>
          <span>
            <div className={specListStyle}>
              {product.name}
            </div>
            {hasMultipleSpecs
              ? <div className={specWrapperStyle}>
                  <div ref={scrollingRef}>
                    {
                      product?.items?.map((item) => {
                        return (
                          <SpecBlock
                            key={item.productItemId}
                            item={item}
                            useButtonStyle={type === ProductViewType.MultiplePrices}
                            isActive={displayItem.productItemId === item.productItemId}
                            onSelected={item => setItem(item)}
                          />
                        )
                      })
                    }
                  </div>
                </div>
              : null}
          </span>
          <SmartLink
            href={landingUrl}
            className={styles.button}
            onClick={outClick}
            title={t('client.shoplist.button.buynow')}
          >
            <div className={styles.chatIcon}/>
            <div className={noticeBubbleStyle}>
              {item
                ? (
                  <NoticeBubble
                    iconUrl="/img/svg/img-defaultHead--grey.svg"
                    text={item}
                    status={t('client.shoplist.desc.wanttobuy')}
                    onAnimationEnd={next}
                  />
                  )
                : null}
            </div>
          </SmartLink>
        </div>
        <div className={styles.itemSet}>
          <span className={styles.strongFonts}>{t('client.shoplist.desc.livediscount')}</span>
          <Price
            className={styles.price}
            price={price}
            currency={displayItem?.currency}
          />
          {
            originalPrice > price
              ? <Price
                  className={styles.originalPrice}
                  price={originalPrice}
                  currency={displayItem?.currency}
                />
              : <></>
          }
        </div>

        <div>
          <div className={styles.subInfo}>
            <Benefit
              benefit={displayItem?.benefit}
            />
            {/* Hide this UI because the translation has not been settled. */}
            {/* <span className={styles.peopleCount}>
              {
                userCount
                  ? (<span>
                    { `${userCount.toLocaleString()} 人搶購` }
                  </span>)
                  : ''
              }
            </span> */}
          </div>
        </div>
      </div>
      <div className={styles.saleWording}>
        {
          saleWording
            ? <>
              <div className={styles.saleWordingIcon}/>
              {saleWording}
            </>
            : null
        }
      </div>
    </div>
  )
}

export default LiveProduct
